/**
 * Draw a diverging-likert plot row with the deck's bar model (plots plan P).
 *
 * The row's segments, bands, net and the exhibit legend are Python's
 * (`figure_plots.diverging_likert_row` / `likert_legend`, bands from
 * `rating_segments.py`). This module only lays them out: the centre line and
 * the piece either side of it (`planRatingBar`), and the swatch colour of each
 * legend band, read off the tones the bars paint. It computes no figure.
 */

import { planRatingBar } from './bar-plan'
import type { RatingBandLabels, RatingBarLegendEntry, RatingBarPlan } from './bar-plan'
import type { StoredPlotPayload } from '../contract'
import { divergingLikertViewLegend, divergingLikertViewPieces } from './stat-view-plan'
import type { PlotStatView } from '@digital-twins/slide-schemas/generated/plot-types'

export type DivergingLikertStoredData = StoredPlotPayload<'diverging-likert'>['data']
export type DivergingLikertStoredRow = DivergingLikertStoredData['rows'][number]

/** A bar the slide draws: its plan and whether it has data to draw. */
export interface DivergingLikertDrawnBar {
  plan: RatingBarPlan | null
  dataStatus: 'available' | 'empty' | 'invalid' | 'unsupported'
}

function bandWording(data: DivergingLikertStoredData): RatingBandLabels {
  const byBand = new Map(data.legend.map((entry) => [entry.band, entry.label]))
  return {
    favorable: byBand.get('favorable') ?? '',
    middle: byBand.get('middle') ?? '',
    unfavorable: byBand.get('unfavorable') ?? '',
    mixed: byBand.get('mixed') ?? '',
  }
}

/** One stamped row as the bar model: pieces from its segments, net as stamped. */
export function planDivergingLikertRow(
  row: DivergingLikertStoredRow,
  data: DivergingLikertStoredData,
  /**
   * The stat view the bar is drawn on (`divergingLikertActiveView`): Top N groups the
   * bar into Bottom N | middle | Top N (decision 20); every other view draws the
   * stamped segments.
   */
  view: PlotStatView,
): DivergingLikertDrawnBar {
  if (data.status === 'unsupported') {
    return { plan: null, dataStatus: 'unsupported' }
  }
  const plan = planRatingBar(
    divergingLikertViewPieces(row, data, view).map((piece) => ({
      label: piece.label, value: piece.value, exactShare: piece.share, band: piece.tone,
    })),
    bandWording(data),
  )
  if (!plan) {
    return { plan: null, dataStatus: 'empty' }
  }
  if (typeof row.net === 'number') {
    plan.net = row.net
  }
  return { plan, dataStatus: 'available' }
}

/**
 * The exhibit legend on the view (`divergingLikertViewLegend`: the box names on
 * Top N, else the bands Python stamped), in its order and wording, with the
 * swatch each band is painted in on these bars. A `mixed` band painted in two soft
 * tones (either side of the 0-100 bar) shows both, as the bar model's own
 * legend does.
 */
export function divergingLikertLegend(
  data: DivergingLikertStoredData,
  plans: Array<RatingBarPlan | null>,
  view: PlotStatView,
): Array<RatingBarLegendEntry> {
  const entries: Array<RatingBarLegendEntry> = []
  for (const { tone: band, label } of divergingLikertViewLegend(data, view)) {
    let color: string | null = null
    for (const plan of plans) {
      for (const piece of plan?.pieces ?? []) {
        if (piece.band !== band || piece.value <= 0) {
          continue
        }
        if (color === null) {
          color = piece.tone.color
        } else if (color !== piece.tone.color && !color.startsWith('linear-gradient')) {
          color = `linear-gradient(90deg, ${color} 50%, ${piece.tone.color} 50%)`
        }
      }
    }
    if (color !== null) {
      entries.push({ band, label, color })
    }
  }
  return entries
}

/**
 * Legacy legend for a stored multi-row slide: every band any row paints, in
 * first-seen order. A lane-merged slide has no pooled parent (its buckets are
 * all zero), so the parent bar's legend was empty and the slide drew none.
 */
export function unionLegend(plans: Array<RatingBarPlan | null>): Array<RatingBarLegendEntry> {
  const entries: Array<RatingBarLegendEntry> = []
  for (const plan of plans) {
    for (const entry of plan?.legend ?? []) {
      const existing = entries.find((candidate) => candidate.band === entry.band)
      if (!existing) {
        entries.push({ ...entry })
      } else if (existing.color !== entry.color && !existing.color.startsWith('linear-gradient')) {
        existing.color = `linear-gradient(90deg, ${existing.color} 50%, ${entry.color} 50%)`
      }
    }
  }
  return entries
}
